// ┌──────────────────────────────────────────────────────────────────────────┐
// │ CONTRACTE D'EXECUCIÓ DELS PROGRAMES (ctx).                                 │
// └──────────────────────────────────────────────────────────────────────────┘
//
// Un PROGRAMA és un mòdul JS l'export per defecte del qual és una factoria
// `create(ctx)` que retorna una CALCULADORA { id, appliesTo, run, ... }.
//
// PRINCIPI D'AÏLLAMENT: un programa s'executa NOMÉS amb el seu `code` + les seves
// dependències. No sap res del component que el corre. Per això `ctx` porta NOMÉS
// DADES — cap servei de codi del motor:
//
//   ctx.deps    → tot recurs extern declarat com a dependència (key → contingut):
//                 · dataset JSON  → objecte ja parsejat
//                 · mòdul JS      → l'export del mòdul (carregat via Blob+import)
//                 Servits pel catàleg (transform_program_dependency); en local
//                 (fallback) els injecta el component.
//   ctx.fields  → camps del formulari carregat.
//   ctx.config  → bag de config (per-programa).
//   ctx.sherpa  → { baseUrl, token }: DADES de connexió al backend de Sherpa, per si
//                 un programa ha de consultar un endpoint (p.ex. enriquiment geogràfic
//                 via XHR SÍNCRON). Segueix sent "només dades": el motor NO injecta cap
//                 client HTTP; el programa fa la crida ell mateix amb aquestes dades.
//
// Si un programa necessita una utilitat, o la inlinea al seu `code` o la declara
// com a dependència JS — mai la rep del component.

// Registre de dependències LOCALS del component: BUIT a propòsit. Cap dataset de
// client viu al bundle — totes les dependències (JSON i JS) les declara cada
// programa al catàleg (transform_program_dependency) i el store les baixa en
// runtime. Es manté el mecanisme (no la data) per si calgués una dep d'infra
// genèrica del propi component en el futur.
const LOCAL_DEP_LOADERS = Object.freeze({});

export function availableDeps() {
  return Object.keys(LOCAL_DEP_LOADERS);
}

// Carrega (en paral·lel) el conjunt de dependències locals demanat → { key: contingut }.
export async function loadDeps(keys = []) {
  const out = {};
  await Promise.all(
    keys.map(async (key) => {
      const loader = LOCAL_DEP_LOADERS[key];
      if (!loader) throw new Error(`Dependència desconeguda: '${key}'.`);
      out[key] = await loader();
    }),
  );
  return out;
}

// Construeix el `ctx` (només dades) que es passa a `create(ctx)`.
export function buildProgramContext({ fields = [], config = {}, deps = {}, sherpa = null } = {}) {
  let s = null;
  if (sherpa) {
    // NORMALITZACIÓ DEL TOKEN: el token pot arribar amb el prefix d'esquema ja posat o
    // net, segons el mode d'execució:
    //   · standalone         → authScheme BEARER, token NU ("xxx")
    //   · embed a SherpaCRM   → authScheme RAW, token PREFIXAT ("Bearer xxx",
    //                           de localStorage['user-token'])
    // Els programes autocontinguts (p.ex. geoEnrich) construeixen la capçalera com
    // 'bearer ' + sherpa.token; si el token ja porta el prefix, sortiria
    // "bearer Bearer xxx" i el backend respon 401. Normalitzem SEMPRE a token nu perquè
    // el programa hi anteposi l'esquema una sola vegada, funcioni quin sigui el mode.
    s = { ...sherpa };
    if (typeof s.token === 'string') s.token = s.token.replace(/^\s*bearer\s+/i, '');
  }
  return Object.freeze({ deps, fields, config, sherpa: s ? Object.freeze(s) : null });
}
